import { useLingui } from "@lingui/react/macro";
import { TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppDialog } from "@/components/common/AppDialog";

interface UnsavedChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Leave anyway — the edits are dropped. */
  onDiscard: () => void;
  /** Stay put and keep them. */
  onStay: () => void;
}

/**
 * Asked before a cook walks away from edits that were never saved (T-707).
 * Staying is the default action, because losing the work is the outcome nobody
 * asked for.
 */
export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onDiscard,
  onStay,
}: UnsavedChangesDialogProps) {
  const { t } = useLingui();

  return (
    <AppDialog open={open} onOpenChange={onOpenChange} hideClose size="sm">
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div className="flex size-20 items-center justify-center rounded-lg bg-(--color-surface-chip-inactive)">
          <TriangleAlert className="size-10" />
        </div>

        <h3 className="text-lg font-semibold">{t`Leave without saving?`}</h3>
        <p className="text-sm text-(--color-text-secondary)">
          {t`This recipe has changes you haven't saved yet. Leaving now discards them.`}
        </p>

        <div className="flex w-full gap-3 pt-2">
          <Button variant="outline" className="flex-1" onClick={onDiscard}>
            {t`Discard changes`}
          </Button>
          <Button className="flex-1" onClick={onStay}>
            {t`Keep editing`}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}
